import React, { useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { LoadingButton } from '@mui/lab';
import { Grid } from '@mui/system';
import useApiMutation from '../../Hooks/useApiMutation';
import {
  formDataProcessor,
  transformKeys,
} from '../Posts/Show/Consultation/Tabs/Contents/FormShow/utils';
import queriesConfig from '../../Services/queriesConfig';
import { FormQuestion } from '../../Utils/Types/FormBuilder';
import { UseFormReturn } from 'react-hook-form';

interface FormSection {
  section: FormQuestion;
  questions: FormQuestion[];
}

interface FormSectionWizardProps {
  post: { id: number };
  form: UseFormReturn<any>;
  setErrors: (errors: Record<string, any>) => void;
  groupedSections: FormSection[];
  children: React.ReactNode;
  loading?: boolean;
}

const FormSectionWizard: React.FC<FormSectionWizardProps> = ({
  post,
  children,
  form,
  groupedSections,
  loading = false,
  setErrors,
}) => {
  const pages = React.Children.toArray(children);
  const totalPages = pages.length;

  const [currentPage, setCurrentPage] = useState(1);
  const [validating, setValidating] = useState(false);

  const deltaRef = useRef(0); // For pagination

  const { mutate: validate } = useApiMutation({
    query: queriesConfig.validateFormResponse(post.id),
    onSuccess: () => {
      setValidating(false);
      setErrors({});
      setCurrentPage((prev) => {
        const next = prev + deltaRef.current;
        return Math.min(Math.max(next, 1), totalPages);
      });
    },
    onError: (responseData: any) => {
      const formatted = transformKeys(responseData);
      setValidating(false);
      setErrors(formatted);
    },
  });

  const handleChangePage = (delta: number) => {
    deltaRef.current = delta;

    const formData = form.getValues();

    // NOTE: We select from raw only the answers from questions that are in the current page to avoid getting wrong indexed errors.
    // As the form states accumulates answers, it sending it all to the backend unless we filter it.
    const currentPageQuestions = groupedSections[currentPage - 1].questions.map((question: any) =>
      question.id.toString(),
    );
    const filteredAnswers = formData.answers_attributes.filter((answer: any) =>
      currentPageQuestions.includes(answer.question_id),
    );

    setValidating(true);

    validate(
      formDataProcessor({ ...formData, answers_attributes: filteredAnswers }, currentPageQuestions),
    );
  };

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  return (
    <div>
      {pages[currentPage - 1]}

      <Grid container sx={{ py: 3 }}>
        <Grid sx={{ mr: 'auto' }}>
          <Typography variant="body1">
            <FormattedMessage id="PAGE_OF" values={{ current: currentPage, total: totalPages }} />
          </Typography>
        </Grid>
        <Grid>
          <LoadingButton
            variant="outlined"
            size="large"
            disabled={isFirstPage || validating}
            loading={validating}
            onClick={() => handleChangePage(-1)}
          >
            <FormattedMessage id="PREVIOUS" />
          </LoadingButton>
        </Grid>
        <Grid>
          {isLastPage ? (
            <LoadingButton
              loading={loading}
              type="submit"
              sx={{ ml: 2 }}
              variant="contained"
              size="large"
              form="post-form-response"
            >
              <FormattedMessage id="BUTTONS.SAVE" />
            </LoadingButton>
          ) : (
            <LoadingButton
              variant="outlined"
              size="large"
              disabled={isLastPage || validating}
              loading={validating}
              onClick={() => handleChangePage(+1)}
              sx={{ ml: 2 }}
            >
              <FormattedMessage id="NEXT" />
            </LoadingButton>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default FormSectionWizard;
